import React from 'react';
import { Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import DownIcon from '../../../../img/ant-down-icon.svg';

/**
 * Selected value tags component for RangeSelect
 */
const SelectedValueTags = ({ value, options, onRemove }) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  
  const tags = value.map((selectedValue) => {
    const option = options.find((opt) => opt.value === selectedValue);
    const displayLabel = option ? option.label : selectedValue;
    return (
      <Tag
        key={selectedValue}
        closable
        onClose={() => onRemove(selectedValue)}
        className="selected-tag"
      >
        {displayLabel}
      </Tag>
    );
  });
  
  return <div className="selected-tags-container">{tags}</div>;
};

/**
 * Range select component for risk level filtering matching ElasticsearchSelect style exactly
 */
const RangeSelect = ({ 
  label, 
  value = [], 
  onChange, 
  placeholder = "Sélectionner un niveau de risque...",
  allowClear = true,
  mode = "multiple",
  maxTagCount = 1,
  style = { width: '100%' },
  showSelectedTags = true,
  ...props
}) => {
  const { t } = useTranslation();

  const riskLevels = [
    { value: 1, label: t('active-substance|risk.Low') },
    { value: 2, label: t('active-substance|risk.Light') },
    { value: 3, label: t('active-substance|risk.Moderate') },
    { value: 4, label: t('active-substance|risk.High') },
    { value: 5, label: t('active-substance|risk.Extremely High') },
  ];

  const handleChange = (newValue) => {
    if (onChange) {
      // Map newValue against our riskLevels to get proper labels (same as ElasticsearchSelect)
      const selectedItems = Array.isArray(newValue)
        ? newValue.map((val) => {
            const option = riskLevels.find((opt) => opt.value === val);
            return option
              ? { value: val, label: option.label }
              : { value: val, label: val };
          })
        : newValue;
      
      onChange(newValue, selectedItems);
    }
  };

  const handleRemoveTag = (valueToRemove) => {
    const newValue = value.filter(v => v !== valueToRemove);
    const remainingOptions = riskLevels.filter(level => newValue.includes(level.value));
    const transformedItems = remainingOptions.map(item => ({
      label: item.label,
      value: item.value,
    }));
    
    if (onChange) {
      onChange(newValue, transformedItems);
    }
  };

  const selectOptions = riskLevels.map(level => ({
    value: level.value,
    label: level.label
  }));

  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        <label className="select-label">{t(label)}</label>
      </div>

      <Select
        className="elasticsearch-select"
        mode={mode}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        allowClear={allowClear}
        style={style}
        maxTagCount={maxTagCount}
        showSearch={false}
        options={selectOptions}
        suffixIcon={<img src={DownIcon} alt="Down" className="down-icon" />}
        {...props}
      />

      {showSelectedTags && (
        <SelectedValueTags
          value={value}
          options={riskLevels}
          onRemove={handleRemoveTag}
        />
      )}
    </div>
  );
};

export default React.memo(RangeSelect);